import { Link } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { useAppConfig } from '../../appConfig';
import { useAuth } from '../../auth';
import { useMe } from '../../hooks/useMe';
import { useWebT } from '../hooks';
import { useSignOut } from './AccountGuard';

const ITEM =
  'block w-full rounded-[8px] px-3 py-2.5 text-left text-[14px] leading-[normal] font-medium text-ink hover:bg-surface-3';

/** E-27 134:3327, under the avatar of W/Nav. Mobile has no menu: its avatar opens `/cuenta`. */
export function AccountMenu({ onClose }: { onClose: () => void }) {
  const t = useWebT();
  const auth = useAuth();
  const me = useMe();
  const config = useAppConfig();
  const signOut = useSignOut();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      // The avatar toggles the menu itself: closing here too would reopen it on the same click.
      if (ref.current?.contains(target) || target?.closest('[data-account-avatar]')) return;
      onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      onClose();
      document.querySelector<HTMLElement>('[data-account-avatar]')?.focus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const name = me.data?.user.displayName ?? auth.user?.displayName;
  const email = auth.user?.email;

  return (
    <nav
      ref={ref}
      aria-label={t('web:auth.menu.label')}
      data-testid="account-menu"
      className="absolute top-[calc(100%+15px)] right-0 z-40 flex w-[260px] flex-col gap-0.5 rounded-[14px] bg-surface-2 p-2"
    >
      <div className="flex min-w-0 flex-col gap-0.5 px-3 py-2.5 leading-[normal]">
        {name ? <p className="m-0 truncate text-[15px] font-bold text-ink">{name}</p> : null}
        {email ? <p className="m-0 truncate text-[13px] text-muted">{email}</p> : null}
      </div>
      <Link to="/cuenta" className={ITEM} onClick={onClose}>
        {t('web:auth.menu.account')}
      </Link>
      <Link to="/mi-musica" className={ITEM} onClick={onClose}>
        {t('web:auth.menu.myMusic')}
      </Link>
      <Link to="/planes" className={ITEM} onClick={onClose}>
        {t('web:auth.menu.plans')}
      </Link>
      {/* `/tv` is the pairing flow of the stick, outside this layout. */}
      <a href="/tv" className={ITEM}>
        {t('web:auth.menu.tvStick')}
      </a>
      <a href={`mailto:${config.supportEmail}`} className={ITEM} onClick={onClose}>
        {t('web:auth.menu.help')}
      </a>
      <button
        type="button"
        className={ITEM}
        onClick={() => {
          onClose();
          void signOut();
        }}
      >
        {t('web:auth.menu.logout')}
      </button>
    </nav>
  );
}
